<script lang="ts">
  import { page } from "$app/stores"
  import { onMount } from "svelte"
  import { fly } from "svelte/transition"

  export let dark = true
  export let session: any // Directly accept session as a prop

  // Reactively update session from parent prop
  $: currentSession = session

  // Define navigation links
  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/services", label: "Services" },
    { href: "/about", label: "About" },
    { href: "/blog", label: "Blog" },
    { href: "/contact", label: "Contact" },
  ]

  // Mobile menu state
  let mobileMenuOpen = false

  // Animation control
  let mounted = false
  onMount(() => {
    mounted = true
  })

  // Scroll behavior
  let scrolled = false
  let lastScrollTop = 0
  let hidden = false

  function handleScroll() {
    const st = window.pageYOffset || document.documentElement.scrollTop
    scrolled = st > 20

    // Hide on scroll down, show on scroll up
    if (st > lastScrollTop && st > 100) {
      hidden = true
    } else {
      hidden = false
    }
    lastScrollTop = st <= 0 ? 0 : st
  }

  onMount(() => {
    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  })
</script>

<svelte:window on:scroll={handleScroll} />

<header
  class="{dark
    ? 'bg-slate-900/90'
    : 'bg-white/90'} backdrop-blur-sm fixed top-0 left-0 w-full z-50 transition-all duration-300 {scrolled
    ? 'shadow-md'
    : ''} {hidden ? '-translate-y-full' : 'translate-y-0'}"
>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="flex justify-between items-center py-4">
      <!-- Logo replaced with text -->
      <div class="flex-shrink-0">
        <a href="/" class="flex items-center">
          <span
            class="{dark
              ? 'text-white'
              : 'text-slate-900'} text-xl font-bold tracking-wide"
          >
            Hollas.net
          </span>
        </a>
      </div>

      <!-- Desktop Navigation -->
      <nav class="hidden md:flex items-center space-x-8">
        {#each navLinks as link}
          <a
            href={link.href}
            class="{dark
              ? 'text-slate-300'
              : 'text-slate-700'} hover:text-blue-500 transition-colors text-sm font-medium {$page
              .url.pathname === link.href
              ? 'active-link'
              : ''}"
          >
            {link.label}
          </a>
        {/each}
        {#if session?.user}
          <a
            href="/account"
            class="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors"
          >
            Account
          </a>
        {:else}
          <a
            href="/login"
            class="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors"
          >
            Login
          </a>
        {/if}
      </nav>

      <!-- Mobile menu button -->
      <div class="md:hidden">
        <button
          type="button"
          class="{dark
            ? 'text-slate-300'
            : 'text-slate-700'} hover:text-blue-500"
          on:click={() => (mobileMenuOpen = !mobileMenuOpen)}
        >
          <span class="sr-only"
            >{mobileMenuOpen ? "Close menu" : "Open menu"}</span
          >
          {#if mobileMenuOpen}
            <svg
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          {:else}
            <svg
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          {/if}
        </button>
      </div>
    </div>
  </div>

  <!-- Mobile Navigation Menu -->
  {#if mobileMenuOpen}
    <div
      class="md:hidden {dark ? 'bg-slate-800' : 'bg-white'}"
      transition:fly={{ y: -20, duration: 200 }}
    >
      <div class="pt-2 pb-4 space-y-1 px-4">
        {#each navLinks as link}
          <a
            href={link.href}
            class="{dark
              ? 'text-slate-200'
              : 'text-slate-700'} hover:text-blue-500 block py-2 text-base font-medium transition-colors {$page
              .url.pathname === link.href
              ? 'active-link'
              : ''}"
            on:click={() => (mobileMenuOpen = false)}
          >
            {link.label}
          </a>
        {/each}
        {#if session?.user}
          <a
            href="/account"
            class="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md inline-block text-base font-medium transition-colors mt-4"
            on:click={() => (mobileMenuOpen = false)}
          >
            Account
          </a>
        {:else}
          <a
            href="/login"
            class="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md inline-block text-base font-medium transition-colors mt-4"
            on:click={() => (mobileMenuOpen = false)}
          >
            Login
          </a>
        {/if}
      </div>
    </div>
  {/if}
</header>

<style>
  .active-link {
    font-weight: bold;
    color: #3b82f6; /* A distinct blue color */
    border-bottom: 2px solid #3b82f6; /* Add an underline for more emphasis */
    padding-bottom: 2px;
  }
</style>
